import React, { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  TextField,
  Alert,
  Divider,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Fade,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Apple as AppleIcon
} from '@mui/icons-material';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { ILoginCredentials, IRegisterCredentials, IPendingSocialAuth } from '../../types/auth';
import SocialAccountConfirmation from './SocialAccountConfirmation';
import { AccountType } from '../../../../shared/types/user';
import NetworkErrorAlert from '../common/NetworkErrorAlert';
import { getErrorMessage, isNetworkError } from '../../utils/errorUtils';
import { loadAppleSignIn, signInWithApple, isAppleSignInCancelled } from '../../services/appleSignIn';
import legal from '../../config/legal.json';
import SignupConsent from './SignupConsent';
const GoogleIcon = () => (
  <SvgIcon viewBox="0 0 18 18" sx={{ fontSize: '20px !important' }}>
    <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2582h2.9086c1.7018-1.5668 2.6837-3.8741 2.6837-6.6155z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.4673-.8059 5.9564-2.18l-2.9086-2.2582c-.8059.54-1.8368.8591-3.0478.8591-2.3441 0-4.3282-1.5832-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z" />
    <path fill="#FBBC05" d="M3.9641 10.7105C3.7841 10.1705 3.6818 9.5932 3.6818 9s.1023-1.1705.2823-1.7105V4.9573H.9573C.3477 6.1723 0 7.5464 0 9s.3477 2.8277.9573 4.0427l3.0068-2.3322z" />
    <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.3459l2.5814-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9573l3.0068 2.3322C4.6718 5.1627 6.6559 3.5795 9 3.5795z" />
  </SvgIcon>
);

// Validation schemas
const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password should be at least 8 characters')
    .required('Password is required'),
});

const registerValidationSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Name should be at least 2 characters')
    .max(50, 'Name should not exceed 50 characters')
    .required('Name is required'),
  username: Yup.string()
    .min(3, 'Username should be at least 3 characters')
    .max(20, 'Username should not exceed 20 characters')
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .required('Username is required'),
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password should be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain uppercase, lowercase, number, and special character'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  acceptTerms: Yup.boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
});

export interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
  onSuccess?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onClose,
  initialMode = 'login',
  onSuccess
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login, googleLogin, appleLogin, completeSocialLogin, register, loading, error, clearError } = useAuth();
  const { success, notifyError } = useNotifications();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [pendingSocial, setPendingSocial] = useState<IPendingSocialAuth | null>(null);
  const socialAttempt = React.useRef(0);
  const appleSignInConfigured = Boolean(
    process.env.REACT_APP_APPLE_CLIENT_ID && process.env.REACT_APP_APPLE_REDIRECT_URI
  );

  React.useEffect(() => {
    if (open && appleSignInConfigured) {
      // Failures are reported on click, without interrupting email/Google login.
      void loadAppleSignIn().catch(() => undefined);
    }
  }, [open, appleSignInConfigured]);
  
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    socialAttempt.current++;
    setPendingSocial(null);
    if (open) {
      setMode(initialMode);
      setNetworkError(null);
      clearError();
      loginFormik.resetForm();
      registerFormik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode]);

  // Handle login submission
  const handleLoginSubmit = async (values: ILoginCredentials) => {
    try {
      clearError();
      setNetworkError(null);
      
      const credentials: ILoginCredentials = {
        ...values,
        rememberMe
      };
      
      await login(credentials);
      // Close the dialog immediately after login succeeds so its backdrop
      // is removed before the deferred navigation triggers a re-render.
      onClose();
      success('Welcome back!');
      onSuccess?.();
    } catch (err) {
      console.error('Login failed:', err);
      if (isNetworkError(err)) {
        setNetworkError(getErrorMessage(err));
      } else if (!error) {
        notifyError(getErrorMessage(err));
      }
    }
  };

  // Handle registration submission
  const handleRegisterSubmit = async (values: Omit<IRegisterCredentials, 'ageConfirmed'>) => {
    try {
      clearError();
      setNetworkError(null);
      
      await register({ ...values, ageConfirmed: values.acceptTerms === true });
      // Close the dialog immediately so its backdrop is removed
      // before the deferred navigation triggers a re-render.
      onClose();
      success('Account created successfully!');
      onSuccess?.();
    } catch (err) {
      console.error('Registration failed:', err);
      if (isNetworkError(err)) {
        setNetworkError(getErrorMessage(err));
      } else if (!error) {
        notifyError(getErrorMessage(err));
      }
    }
  };

  // Login formik
  const loginFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginValidationSchema,
    onSubmit: handleLoginSubmit,
  });

  // Register formik
  const registerFormik = useFormik<Omit<IRegisterCredentials, 'ageConfirmed'>>({
    initialValues: {
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
      accountType: 'regular' as AccountType,
      businessName: '',
    },
    validationSchema: registerValidationSchema,
    onSubmit: handleRegisterSubmit,
  });

  // Handle tab change
  const handleModeChange = (_event: React.SyntheticEvent, newMode: 'login' | 'register') => {
    setMode(newMode);
    clearError();
    setNetworkError(null);
  };

  // Google login handler
  const googleLoginHook = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);
        clearError();
        setNetworkError(null);

        // Exchange the authorization code for an ID token via our backend
        // useGoogleLogin with flow='implicit' returns an access_token
        // We need to get the id_token by using the 'auth-code' flow or the credential from One Tap
        // For implicit flow, we send the access_token and handle it server-side
        const attempt = ++socialAttempt.current;
        const pending = await googleLogin(tokenResponse.access_token);
        if (attempt !== socialAttempt.current) return;
        if (pending) {
          setPendingSocial(pending);
          return;
        }
        // Close dialog immediately, before deferred navigation fires
        onClose();
        success('Welcome!');
        onSuccess?.();
      } catch (err) {
        console.error('Google login failed:', err);
        if (isNetworkError(err)) {
          setNetworkError(getErrorMessage(err));
        } else {
          notifyError(getErrorMessage(err));
        }
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google login error:', errorResponse);
      notifyError('Google login failed. Please try again.');
    },
    flow: 'implicit'
  });  

  const handleGoogleLogin = useCallback(() => {
    googleLoginHook();
  }, [googleLoginHook]);

  const handleAppleLogin = async () => {
    if (!appleSignInConfigured) return;
    try {
      setAppleLoading(true);
      clearError();
      setNetworkError(null);
      const credentials = await signInWithApple();
      const attempt = ++socialAttempt.current;
      const pending = await appleLogin(credentials);
      if (attempt !== socialAttempt.current) return;
      if (pending) {
        setPendingSocial(pending);
        return;
      }
      onClose();
      success('Welcome!');
      onSuccess?.();
    } catch (err) {
      if (isAppleSignInCancelled(err)) return;
      if (isNetworkError(err)) {
        setNetworkError(getErrorMessage(err));
      } else {
        notifyError(err instanceof Error ? getErrorMessage(err) : 'Apple sign-in failed. Please allow popups and try again.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  // Retry after network error
  const handleRetry = () => {
    setNetworkError(null);
    if (mode === 'login' && loginFormik.isValid) {
      loginFormik.submitForm();
    } else if (mode === 'register' && registerFormik.isValid) {
      registerFormik.submitForm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading || googleLoading || appleLoading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      // Ensure the portal content (backdrop + dialog) is fully removed
      // from the DOM once the exit transition ends. Without this the
      // invisible elements stay in the tree and capture clicks.
      keepMounted={false}
      disableScrollLock={false}
      TransitionProps={{ unmountOnExit: true }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      {/* Header with gradient background */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 3,
          px: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={loading || googleLoading || appleLoading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            '&:hover': {
              bgcolor: alpha('#fff', 0.1)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box textAlign="center" mt={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {mode === 'login' ? 'Welcome Back' : 'Join Us'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {mode === 'login' 
              ? 'Sign in to continue to your account' 
              : 'Create an account to get started'}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {pendingSocial ? (
          <SocialAccountConfirmation
            key={pendingSocial.pendingToken}
            pending={pendingSocial}
            complete={completeSocialLogin}
            onCancel={() => { setPendingSocial(null); clearError(); }}
            onSuccess={() => { setPendingSocial(null); onClose(); success('Welcome!'); onSuccess?.(); }}
          />
        ) : (
          <>
        {/* Tabs */}
        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2,
              textTransform: 'none',
              fontWeight: 600,
            }
          }}
        >
          <Tab label="Sign In" value="login" />
          <Tab label="Sign Up" value="register" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <Typography variant="caption" component="p" sx={{ mb: 2 }}>
            By creating an account, you agree to our{' '}
            <Link href={legal.termsUrl} target="_blank" rel="noopener noreferrer">Terms</Link>
            {' '}and acknowledge our{' '}
            <Link href={legal.privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
            {' '}{legal.safetyNotice} Legal revision: {legal.version}.
          </Typography>
          {/* Social Login Buttons */}
          <Stack spacing={1.5} mb={3} sx={{ '& .MuiButton-root': { ml: '0px !important' } }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={googleLoading || appleLoading || loading}
              startIcon={googleLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#747775',
                bgcolor: '#fff',
                color: '#1f1f1f',
                justifyContent: 'center',
                '& .MuiButton-startIcon': { position: 'absolute', left: 16 },
                '&:hover': {
                  borderColor: '#747775',
                  bgcolor: '#f2f2f2'
                },
                '&.Mui-disabled': {
                  borderColor: '#e3e3e3',
                  bgcolor: '#f2f2f2',
                  color: '#6b6b6b'
                }
              }}
            >
              {googleLoading ? 'Signing in...' : 'Continue with Google'}
            </Button>
            {appleSignInConfigured && (
              <Button
                fullWidth
                variant="outlined"
                disabled={appleLoading || googleLoading || loading}
                startIcon={appleLoading ? <CircularProgress size={20} color="inherit" /> : <AppleIcon sx={{ fontSize: '20px !important' }} />}
                onClick={handleAppleLogin}
                sx={{
                  py: 1.25,
                  minHeight: 44,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: 'divider',
                  bgcolor: '#000',
                  color: '#fff',
                  justifyContent: 'center',
                  '& .MuiButton-startIcon': { position: 'absolute', left: 16 },
                  '&:hover': {
                    borderColor: 'text.secondary',
                    bgcolor: '#1a1a1a'
                  }
                }}
              >
                {appleLoading ? 'Signing in with Apple...' : 'Continue with Apple'}
              </Button>
            )}
          </Stack>

          {/* Divider */}
          <Divider sx={{ my: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              or continue with email
            </Typography>
          </Divider>

          {/* Network Error Alert */}
          {networkError && (
            <Box mb={2}>
              <NetworkErrorAlert
                message={networkError}
                onRetry={handleRetry}
              />
            </Box>
          )}
          
          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Fade in={mode === 'login'} unmountOnExit>
            <Box sx={{ display: mode === 'login' ? 'block' : 'none' }}>
              <form onSubmit={loginFormik.handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    id="login-email"
                    name="email"
                    label="Email"
                    variant="outlined"
                    value={loginFormik.values.email}
                    onChange={loginFormik.handleChange}
                    onBlur={loginFormik.handleBlur}
                    error={loginFormik.touched.email && Boolean(loginFormik.errors.email)}
                    helperText={loginFormik.touched.email && loginFormik.errors.email}
                    disabled={loading}
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />

                  <TextField
                    fullWidth
                    id="login-password"
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    value={loginFormik.values.password}
                    onChange={loginFormik.handleChange}
                    onBlur={loginFormik.handleBlur}
                    error={loginFormik.touched.password && Boolean(loginFormik.errors.password)}
                    helperText={loginFormik.touched.password && loginFormik.errors.password}
                    disabled={loading}
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          size="small"
                          disabled={loading}
                        />
                      }
                      label={<Typography variant="body2">Remember me</Typography>}
                    />
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      variant="body2"
                      underline="hover"
                      onClick={onClose}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Fade>

          {/* Register Form */}
          <Fade in={mode === 'register'} unmountOnExit>
            <Box sx={{ display: mode === 'register' ? 'block' : 'none' }}>
              <form onSubmit={registerFormik.handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    id="register-name"
                    name="name"
                    label="Full Name"
                    variant="outlined"
                    size="small"
                    value={registerFormik.values.name}
                    onChange={registerFormik.handleChange}
                    onBlur={registerFormik.handleBlur}
                    error={registerFormik.touched.name && Boolean(registerFormik.errors.name)}
                    helperText={registerFormik.touched.name && registerFormik.errors.name}
                    disabled={loading}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />

                  <TextField
                    fullWidth
                    id="register-username"
                    name="username"
                    label="Username"
                    variant="outlined"
                    size="small"
                    value={registerFormik.values.username}
                    onChange={registerFormik.handleChange}
                    onBlur={registerFormik.handleBlur}
                    error={registerFormik.touched.username && Boolean(registerFormik.errors.username)}
                    helperText={registerFormik.touched.username && registerFormik.errors.username}
                    disabled={loading}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />

                  <TextField
                    fullWidth
                    id="register-email"
                    name="email"
                    label="Email"
                    variant="outlined"
                    size="small"
                    value={registerFormik.values.email}
                    onChange={registerFormik.handleChange}
                    onBlur={registerFormik.handleBlur}
                    error={registerFormik.touched.email && Boolean(registerFormik.errors.email)}
                    helperText={registerFormik.touched.email && registerFormik.errors.email}
                    disabled={loading}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />

                  <TextField
                    fullWidth
                    id="register-password"
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    size="small"
                    value={registerFormik.values.password}
                    onChange={registerFormik.handleChange}
                    onBlur={registerFormik.handleBlur}
                    error={registerFormik.touched.password && Boolean(registerFormik.errors.password)}
                    helperText={registerFormik.touched.password && registerFormik.errors.password}
                    disabled={loading}
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    id="register-confirmPassword"
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    variant="outlined"
                    size="small"
                    value={registerFormik.values.confirmPassword}
                    onChange={registerFormik.handleChange}
                    onBlur={registerFormik.handleBlur}
                    error={registerFormik.touched.confirmPassword && Boolean(registerFormik.errors.confirmPassword)}
                    helperText={registerFormik.touched.confirmPassword && registerFormik.errors.confirmPassword}
                    disabled={loading}
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <SignupConsent
                    checked={registerFormik.values.acceptTerms}
                    onChange={checked => { void registerFormik.setFieldValue('acceptTerms', checked); }}
                    disabled={loading}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading || !registerFormik.values.acceptTerms}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Fade>
        </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
